import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons/faExclamationTriangle';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';
import { faGlobe } from '@fortawesome/pro-solid-svg-icons/faGlobe';
import { faHouse } from '@fortawesome/pro-solid-svg-icons/faHouse';
import { faBuilding } from '@fortawesome/pro-solid-svg-icons/faBuilding';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { defineMessages, FormattedMessage } from 'react-intl';

import { InteractablePopover } from 'ui-components/components/interactable-popover';

import { VER, UNV, WRN, ERR } from '../../../enums/address-verification-status';
import { ResidentialIndicator, isCommercial, isResidential } from '../../../enums/residential-indicator';
import { AddressVerificationStatusPropType } from '../../../prop-types/address-verification-status';
import { ResidentialIndicatorPropType } from '../../../prop-types/residential-indicator';
import CustomerSummaryPopover from '../../../components/customer-summary';
import { emptyObject } from 'ui-components/default-props/empty';
import { useHideCustomerSummaryPopover } from '../../../hooks/use-user-setting';

import style from './recipient-column.module.less';

const messages = defineMessages({
  internationalAddressValidated: {
    description: 'recipientColumn.internationalAddressValidated',
    defaultMessage: 'International Address Validated',
    id: 'neOseJ',
  },
  residentialAddressValidated: {
    description: 'recipientColumn.residentialAddressValidated',
    defaultMessage: 'Residential Address Validated',
    id: 'T7B5JH',
  },
  commercialAddressValidated: {
    description: 'recipientColumn.commercialAddressValidated',
    defaultMessage: 'Commercial Address Validated',
    id: 'NSGCMr',
  },
  addressValidated: {
    description: 'recipientColumn.addressValidated',
    defaultMessage: 'Address Validated',
    id: '2ThfyR',
  },
  addressErrorTitle: {
    description: 'title to display on the flyout popover',
    defaultMessage: 'Address Verification Failed',
    id: 'PiSn7X',
  },
  defaultAddressErrorMessage: {
    description: 'default message to display if no error message',
    defaultMessage: 'Address has not been verified',
    id: 'rtO4WL',
  },
});

const addressVerificationToIconState = {
  [UNV]: {
    icon: null,
    iconClassName: '',
  },
  [VER]: {
    icon: null,
    iconClassName: style.verified,
  },
  [WRN]: {
    icon: faExclamationTriangle,
    iconClassName: style.warning,
  },
  [ERR]: {
    icon: faExclamationCircle,
    iconClassName: style.error,
  },
};

const getVerifiedIcon = (residentialIndicator, isDomestic) => {
  if (!isDomestic) {
    return faGlobe;
  }
  if (isResidential(residentialIndicator)) {
    return faHouse;
  }
  return faBuilding;
};

const getTooltipText = (isDomestic, residentialIndicator, verificationStatus, verificationMessage) => {
  if (!verificationStatus || verificationStatus === UNV) {
    return '';
  }
  if (verificationStatus === WRN || verificationStatus === ERR) {
    return verificationMessage || <FormattedMessage {...messages.defaultAddressErrorMessage} />;
  }
  if (verificationStatus !== VER) {
    return '';
  }
  if (!isDomestic) {
    return <FormattedMessage {...messages.internationalAddressValidated} />;
  }
  if (isResidential(residentialIndicator)) {
    return <FormattedMessage {...messages.residentialAddressValidated} />;
  }
  if (isCommercial(residentialIndicator)) {
    return <FormattedMessage {...messages.commercialAddressValidated} />;
  }
  return <FormattedMessage {...messages.addressValidated} />;
};

/**
 * @param {object} props
 * @param {boolean} [props.isDomestic]
 * @param {import('../../../prop-types/residential-indicator').default} [props.residentialIndicator]
 * @param {import('../../../prop-types/address-verification-status').default} [props.verificationStatus]
 * @param {string} [props.recipientName]
 * @param {string} [props.destinationCountryCode]
 * @param {string} [props.verificationMessage]
 * @param {string} [props.customerId]
 * @returns {JSX.Element}
 */
const RecipientColumn = ({
  isDomestic = true,
  residentialIndicator = ResidentialIndicator.Commercial,
  verificationStatus = UNV,
  recipientName = '',
  destinationCountryCode = '',
  verificationMessage = '',
  customerId = '',
}) => {
  const showCountry = verificationStatus === VER && !isDomestic && destinationCountryCode;
  const iconState = addressVerificationToIconState[verificationStatus] || emptyObject;
  const { iconClassName = '' } = iconState;
  const icon = iconState.icon || getVerifiedIcon(residentialIndicator, isDomestic);
  const hideCustomerSummaryPopover = useHideCustomerSummaryPopover();
  const addressIsVerified = verificationStatus === UNV || verificationStatus === VER;
  const toolTipMessage = useMemo(
    () => getTooltipText(isDomestic, residentialIndicator, verificationStatus, verificationMessage),
    [isDomestic, residentialIndicator, verificationMessage, verificationStatus],
  );

  return (
    <div className={style['recipient-column']}>
      {verificationStatus !== UNV && (
        <InteractablePopover
          ignoreFlyout={verificationStatus === VER}
          popoverBody={toolTipMessage}
          preferPlace='bottom'
          title={<FormattedMessage {...messages.addressErrorTitle} />}
          useMessagesForTooltipText={!!addressIsVerified}
        >
          <div className={classnames(style['verification-icon'])}>
            <div className={style['icon-wrapper']}>
              <FontAwesomeIcon className={classnames(style.icon, iconClassName)} icon={icon} />
            </div>
            {showCountry && <div className={style['verified-country']}>{destinationCountryCode}</div>}
          </div>
        </InteractablePopover>
      )}
      <CustomerSummaryPopover
        customerId={customerId}
        hideCustomerSummaryPopover={hideCustomerSummaryPopover}
        recipientName={recipientName}
      />
    </div>
  );
};

RecipientColumn.propTypes = {
  isDomestic: PropTypes.bool,
  residentialIndicator: ResidentialIndicatorPropType,
  verificationStatus: AddressVerificationStatusPropType,
  recipientName: PropTypes.string,
  destinationCountryCode: PropTypes.string,
  verificationMessage: PropTypes.string,
  customerId: PropTypes.string,
};

export default React.memo(RecipientColumn);
